import React, { useState, useRef } from 'react';
import LocationList, { LocationType } from '../../LocationList';
import ModeHelpTooltip from './components/ModeHelpTooltip';
import ManualInputForm from './components/ManualInputForm';
import SubmodeSelector from './components/SubmodeSelector';
import SetupActions from './components/SetupActions';
import { 
  importLocationsFromJson,
  SETUP_SUBMODES,
  isSetupMode
} from '../../../Services';
import { useTsp } from '../../../context/TspContext';
import '../../styles/modes/setupMode.css';

export default function SetupMode() {

  const { 
    locations,
    startId,
    setupSubmode,
    handleSubmodeSwitching,
    setLocations,
    setStartId,
    setPath,
    currentModeRef
  } = useTsp();
  
  // Manual input form data
  const [newLocation, setNewLocation] = useState({
    latitude: '',
    longitude: ''
  });
  // Error message
  const [error, setError] = useState('');
  // Import error message
  const [importError, setImportError] = useState('');
  // File input reference
  const fileInputRef = useRef(null);
  // Show help tooltip state
  const [showHelp, setShowHelp] = useState(false);

  // Switch submode
  const switchSubmode = (mode) => {
    handleSubmodeSwitching(mode);
  };
  
  // Toggle help tooltip
  const toggleHelp = (e) => {
    e.preventDefault();
    setShowHelp(!showHelp);
  };

  // Handle file import click
  const handleImportClick = () => {
    // Clear previous error
    setImportError('');
    // Trigger file selection dialog
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle file change/upload
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      // Validate file type
      if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
        throw new Error('Please upload a JSON format file');
      }

      // Import location points
      const importedLocations = await importLocationsFromJson(file);
      
      // Renumber location points while preserving original types
      const renumberedLocations = importedLocations.map((loc, index) => ({
        ...loc,
        name: `${index + 1}`
      }));
      
      // Update location list
      setLocations(renumberedLocations);
      
      // Check if there's a location point marked as start point
      const startPoint = renumberedLocations.find(loc => loc.type === LocationType.START);
      if (startPoint) {
        // If a start point is found, set its ID as the start point
        setStartId(startPoint.id);
      } else {
        // If no start point is found, don't automatically set a start point
        setStartId('');
      }
      
      // Reset file input to allow re-uploading the same file
      e.target.value = '';
    } catch (error) {
      console.error('Failed to import location points:', error);
      setImportError(error.message || 'Failed to import location points');
      // Reset file input
      e.target.value = '';
    }
  };

  // Use passed setupSubmode instead of local state
  const displaySubmode = setupSubmode || SETUP_SUBMODES.MAP;

  return (
    <>
      <div className="mode-description">
        <div className="mode-title-container">
          <h3>Setup Points</h3>
          <button 
            className="mode-help-button"
            onClick={toggleHelp}
            title="Help for Setup Points mode"
          >
            ?
          </button>
        </div>
        <p>Please add location points to visit and set a start point.</p>
      </div>
      
      {/* Help tooltip */}
      {showHelp && <ModeHelpTooltip onClose={() => setShowHelp(false)} />}
      
      {/* Submode selector with file input */}
      <SubmodeSelector 
        displaySubmode={displaySubmode}
        switchSubmode={switchSubmode}
        handleImportClick={handleImportClick}
        fileInputRef={fileInputRef}
        handleFileChange={handleFileChange}
      />
      
      {/* Import error message */}
      {importError && (
        <div className="error-message">
          Import Error: {importError}
        </div>
      )}
      
      {/* Manual input form - only shown in manual input mode */}
      {displaySubmode === SETUP_SUBMODES.MANUAL && (
        <ManualInputForm 
          newLocation={newLocation}
          setNewLocation={setNewLocation}
          error={error}
          setError={setError}
          locations={locations}
          setLocations={setLocations}
        />
      )}
      
      {/* Use LocationList component to manage path points */}
      <LocationList 
        locations={locations}
        startId={startId}
        mode="Setup Points"
        readOnly={false}
        showActions={true}
        allowDelete={true}
        allowSetStart={true}
        onLocationChange={setLocations}
        onStartIdChange={setStartId}
      />
      
      {/* Reset and Download buttons */}
      <SetupActions 
        locations={locations}
        setLocations={setLocations}
        setStartId={setStartId}
        setPath={setPath}
        currentModeRef={currentModeRef}
        isSetupMode={isSetupMode}
      />
    </>
  );
} 